import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { FileText } from 'lucide-react';
import { UploadPDF, GetUserPDF } from '../../Api';

const PDFModal = ({ isOpen, closeModal, userId }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [assignedPDFs, setAssignedPDFs] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categories, setCategories] = useState('');

  // Fetch assigned PDFs on modal open
  useEffect(() => {
    if (isOpen && userId) {
      GetUserPDF(userId)
        .then((result) => setAssignedPDFs(result.data))
        .catch((error) =>
          console.error('Error fetching assigned PDFs:', error)
        );
    }
  }, [isOpen, userId]);

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    setFileType(file.type.split('/')[1]);
  };

  // Handle file upload
  const handleAssignClick = async () => {
    if (!selectedFile) {
      alert('Please select a file first!');
      return;
    }
    if (!title) {
      alert('Please enter a title!');
      return;
    }

    try {
      await UploadPDF(userId, title, description, categories, selectedFile);
      const updatedPDFs = await GetUserPDF(userId);
      setAssignedPDFs(updatedPDFs.data);
      // Reset all fields after successful upload
      setSelectedFile(null);
      setFileType(null);
      setTitle('');
      setDescription('');
      setCategories('');
    } catch (error) {
      console.error('Error uploading PDF:', error);
    }
  };

  return (
    isOpen && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 bg-black/70 flex justify-center items-center z-50"
      >
        <motion.div
          initial={{ y: '-100vh' }}
          animate={{ y: 0 }}
          exit={{ y: '-100vh' }}
          transition={{ duration: 0.3 }}
          className="bg-gradient-to-br from-gray-800 to-black p-6 rounded-lg w-full h-full max-w-none max-h-none overflow-auto"
        >
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-white">Assign PDF</h2>

            <div className="mb-6">
              <label className="block text-gray-300 text-sm font-medium">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-2 p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded w-full focus:outline-none focus:ring focus:ring-blue-500"
              />
            </div>

            <div className="mb-6">
              <label className="block text-gray-300 text-sm font-medium">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-2 p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded w-full focus:outline-none focus:ring focus:ring-blue-500 h-32 resize-none"
              />
            </div>

            <div className="mb-6">
              <label className="block text-gray-300 text-sm font-medium">Categories</label>
              <input
                type="text"
                value={categories}
                onChange={(e) => setCategories(e.target.value)}
                placeholder="Enter categories separated by commas"
                className="mt-2 p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded w-full focus:outline-none focus:ring focus:ring-blue-500"
              />
            </div>

            <div className="mb-6">
              <label className="block text-gray-300 text-sm font-medium">Select File</label>
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                className="mt-2 p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded w-full focus:outline-none focus:ring focus:ring-blue-500"
              />
            </div>

            {fileType && (
              <div className="flex items-center mb-6">
                <FileText size={24} className="text-blue-400 mr-2" />
                <span className="text-gray-400">File type: {fileType}</span>
              </div>
            )}

            <div className="flex justify-between mt-8">
              <button
                onClick={closeModal}
                className="py-3 px-6 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignClick}
                className="py-3 px-6 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all duration-200"
              >
                Assign
              </button>
            </div>

            <div className="mt-8">
              <h3 className="text-xl font-semibold mb-4 text-white">Assigned PDFs</h3>
              {assignedPDFs.length > 0 ? (
                <ul className="space-y-4">
                  {assignedPDFs.map((pdf) => (
                    <li
                      key={pdf.id}
                      className="flex items-center justify-between p-4 bg-gray-700 hover:bg-gray-800 rounded-lg transition-all duration-200"
                    >
                      <div className="flex items-center space-x-4">
                        <FileText size={24} className="text-blue-400" />
                        <div>
                          <a
                            href={pdf.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 underline hover:text-blue-500"
                          >
                            {pdf.fileName}
                          </a>
                          {pdf.title && (
                            <p className="text-sm text-gray-400">{pdf.title}</p>
                          )}
                        </div>
                      </div>
                      <span className="text-sm text-gray-400">
                        {new Date(pdf.uploadDate).toLocaleDateString()}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-400">No PDFs assigned yet.</p>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    )
  );
};

export default PDFModal;